/**
 * Connectivity — single source of truth for "are we online?".
 *
 * WHY THIS EXISTS
 * On the web, `navigator.onLine` plus the `online`/`offline` window events
 * are reliable enough. Inside a Capacitor WebView they are NOT: on Android in
 * particular `navigator.onLine` frequently stays stuck `false` after the app
 * regains connectivity, and the `online` event often never fires at all. The
 * native lifecycle layer already knows the truth (via `@capacitor/network`)
 * and re-broadcasts it as a `chai:network-status` CustomEvent — but until now
 * nothing consumed it, so the whole offline stack (outbox drain, kiosk warm-up,
 * status pills) keyed off the flaky `navigator.onLine` and never noticed the
 * device came back. Queued attendance taps then sat in the outbox forever.
 *
 * This module unifies both signals:
 *   • `isOnline()`          — best-known connectivity, native-aware.
 *   • `subscribeConnectivity(cb)` — fires on ANY change (web DOM events +
 *                             native `chai:network-status`), passing the new
 *                             online state.
 *   • `onConnectivityRestored(cb)` — fires only on an offline→online edge,
 *                             which is exactly when a drain should kick off.
 *
 * On native we seed the cache once from `Network.getStatus()` and keep it
 * fresh from the `chai:network-status` event dispatched by native-lifecycle,
 * so we never add a second Capacitor listener.
 */
import { isNative } from "./platform";

const NATIVE = isNative();

// Last known native connectivity. `null` until seeded — until then we defer to
// `navigator.onLine`, which is correct for the first paint on web and a safe
// optimistic default on native (we'd rather attempt a request than wrongly
// queue it).
let nativeOnline: boolean | null = null;

interface NativeNetworkStatusDetail {
  connected?: boolean;
}

function readNavigatorOnline(): boolean {
  return typeof navigator === "undefined" ? true : navigator.onLine;
}

/** Best-known connectivity. Native status wins on native; web falls back to the DOM flag. */
export function isOnline(): boolean {
  if (NATIVE && nativeOnline !== null) return nativeOnline;
  return readNavigatorOnline();
}

// ── Subscription fan-out ─────────────────────────────────────────────────────

type Listener = (online: boolean) => void;
const listeners = new Set<Listener>();
let installed = false;

function emit(online: boolean): void {
  for (const cb of listeners) {
    try {
      cb(online);
    } catch {
      /* a bad subscriber must not break connectivity fan-out */
    }
  }
}

function handleWebOnline(): void {
  if (!NATIVE) nativeOnline = null; // web: trust the DOM flag directly
  emit(isOnline());
}

function handleWebOffline(): void {
  if (!NATIVE) nativeOnline = null;
  emit(isOnline());
}

function handleNativeStatus(event: Event): void {
  const detail = (event as CustomEvent<NativeNetworkStatusDetail>).detail;
  if (detail && typeof detail.connected === "boolean") {
    nativeOnline = detail.connected;
  }
  emit(isOnline());
}

function ensureInstalled(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;

  window.addEventListener("online", handleWebOnline);
  window.addEventListener("offline", handleWebOffline);
  // Dispatched by native-lifecycle's `@capacitor/network` listener.
  window.addEventListener("chai:network-status", handleNativeStatus);

  // Seed the native cache once so `isOnline()` is accurate before the first
  // status-change event arrives. Best-effort — a failure just leaves us on the
  // `navigator.onLine` fallback.
  if (NATIVE) {
    void import("@capacitor/network")
      .then(({ Network }) => Network.getStatus())
      .then((status) => {
        nativeOnline = status.connected;
        emit(isOnline());
      })
      .catch(() => {
        /* plugin unavailable — stay on navigator.onLine */
      });
  }
}

/**
 * Subscribe to connectivity changes. The callback receives the new online
 * state on every transition (and never the initial state — read `isOnline()`
 * for that). Returns an unsubscribe function.
 */
export function subscribeConnectivity(cb: Listener): () => void {
  ensureInstalled();
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

/**
 * Fire `cb` only when connectivity is restored (offline→online edge). This is
 * the trigger the outbox drain wants — running on every flap would be wasteful,
 * and running only on the DOM `online` event misses the native case entirely.
 * Returns an unsubscribe function.
 */
export function onConnectivityRestored(cb: () => void): () => void {
  let wasOnline = isOnline();
  return subscribeConnectivity((online) => {
    if (online && !wasOnline) cb();
    wasOnline = online;
  });
}

// Install listeners and seed the native cache eagerly at import time so
// `isOnline()` is native-accurate for early callers (e.g. the axios
// `isNetworkUnavailable` check) that never subscribe.
ensureInstalled();
